import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { companyName, tabDescription, tabTitles } from "../constants/pageTitles";
import { paths } from "../constants/paths";
import type { Language } from "../typings";

const siteUrl = "https://agwipol.pl";

const setMeta = (selector: string, attribute: string, value: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    const [name, property] = selector.includes("property=")
      ? [undefined, selector.match(/property="([^"]+)"/)?.[1]]
      : [selector.match(/name="([^"]+)"/)?.[1], undefined];
    if (name) element.name = name;
    if (property) element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
};

const upsertJsonLd = (id: string, value: object) => {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(value);
};

const pageName = (pathname: string, language: Language) =>
  tabTitles[pathname]?.[language] ?? companyName;

const serviceNames: Record<Language, string[]> = {
  Russian: [
    "Карта побыту",
    "Гражданство Польши",
    "Транскрипция документов ЗАГС",
    "Дополнительные услуги",
  ],
  Polish: [
    "Karta pobytu",
    "Obywatelstwo polskie",
    "Transkrypcja aktów stanu cywilnego",
    "Usługi dodatkowe",
  ],
};

const Seo = ({ language }: { language: Language }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isKnownPage = Object.values(paths).includes(pathname);
    const canonicalPath = pathname;
    const canonicalUrl = `${siteUrl}${canonicalPath}`;
    const title = isKnownPage
      ? pageName(canonicalPath, language)
      : `404 | ${companyName}`;
    const description = tabDescription[language];

    document.documentElement.lang = language === "Polish" ? "pl" : "ru";
    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta(
      'meta[name="robots"]',
      "content",
      isKnownPage ? "index,follow" : "noindex,follow",
    );
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta(
      'meta[property="og:locale"]',
      "content",
      language === "Polish" ? "pl_PL" : "ru_RU",
    );

    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const organization = {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      name: "AgwiPol Sp. z o.o.",
      url: `${siteUrl}/`,
      logo: `${siteUrl}/jobs.ico`,
      email: "biuro@agwipol.pl",
      telephone: ["+48574497249", "+48578290011"],
      address: {
        "@type": "PostalAddress",
        streetAddress: "ul. Kard. St. Wyszyńskiego 2/1, lok. 302-303",
        postalCode: "15-888",
        addressLocality: "Białystok",
        addressCountry: "PL",
      },
      sameAs: [
        "https://www.facebook.com/profile.php?id=61550603774103",
        "https://www.instagram.com/agwi_pol/",
      ],
    };

    const graph: object[] = [
      organization,
      {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#localbusiness`,
        name: "AgwiPol Sp. z o.o.",
        url: `${siteUrl}/`,
        image: `${siteUrl}/jobs.ico`,
        parentOrganization: { "@id": `${siteUrl}/#organization` },
        email: "biuro@agwipol.pl",
        telephone: "+48574497249",
        address: organization.address,
      },
      {
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: title,
        description,
        inLanguage: language === "Polish" ? "pl-PL" : "ru-RU",
        publisher: { "@id": `${siteUrl}/#organization` },
        author: { "@id": `${siteUrl}/#organization` },
      },
    ];

    if (isKnownPage && canonicalPath !== paths.home) {
      graph.push({
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: language === "Polish" ? "Strona główna" : "Главная",
            item: `${siteUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: title,
            item: canonicalUrl,
          },
        ],
      });
    }

    if (canonicalPath === paths.services) {
      graph.push(
        ...serviceNames[language].map((name, index) => ({
          "@type": "Service",
          "@id": `${canonicalUrl}#${["residence", "citizenship", "transcription", "other"][index]}`,
          name,
          url: `${canonicalUrl}#${["residence", "citizenship", "transcription", "other"][index]}`,
          provider: { "@id": `${siteUrl}/#organization` },
          areaServed: { "@type": "Country", name: "Poland" },
        })),
      );
    }

    upsertJsonLd("site-structured-data", {
      "@context": "https://schema.org",
      "@graph": graph,
    });
  }, [language, pathname]);

  return null;
};

export default Seo;
